import { create } from 'zustand';
import { axiosInstance } from '../services/axios-instance';
import { UsersService } from '../services/user-service/service';
import { parseAxiosError } from '../utils';
import type {
  User,
  UserList,
  UserUpdate,
} from '../services/user-service/types';
import { PaginateOptions } from '../services/types';

interface UsersState {
  users: User[];
  nextPage?: number;
  prevPage?: number;
  currentUser: User | null;
  currentPage: number;
  totalPages: number;
  serverError: string;
  loadingUsers: boolean;
  count: number;
  totalCount: number;
  listUsers: { (options: PaginateOptions): Promise<UserList> };
  getUserDetails: { (id: number): Promise<User> };
  clearCurrentUser: { (): void };
  updateUser: { (id: number, data: UserUpdate): Promise<void> };
  deleteUser: ({ id }: { id: string | number }) => Promise<void>;
}

const initialState = {
  users: [] as User[],
  nextPage: undefined,
  prevPage: undefined,
  currentUser: null,
  loadingResults: false,
  currentPage: 1,
  totalPages: 1,
  count: 0,
  totalCount: 0,
  loadingUsers: false,
  totalItems: 0,
  serverError: '',
};

const usersService = new UsersService(axiosInstance);

export const useUserStore = create<UsersState>(set => ({
  ...initialState,
  listUsers: async options => {
    set(() => ({ loadingUsers: true }));
    let data: UserList;
    try {
      data = await usersService.list(options);
    } catch (error) {
      set(() => ({
        serverError: parseAxiosError(error),
        loadingUsers: false,
      }));
      throw error;
    }
    set(() => ({
      users: data.items,
      nextPage: data.nextPage,
      prevPage: data.prevPage,
      currentPage: Number(data.page),
      totalPages: Number(data.totalPages),
      count: data.itemCount,
      totalCount: data.totalItems,
      loadingUsers: false,
      serverError: '',
    }));
    return data;
  },
  clearCurrentUser: () => set(() => ({ currentUser: null })),
  getUserDetails: async id => {
    set(() => ({ loadingUsers: true }));
    const user = await usersService.getById({ id });
    set(() => ({ currentUser: user, loadingUsers: false }));
    return user;
  },
  updateUser: async (id, data) => {
    set(() => ({ loadingUsers: true }));
    try {
      const user = await usersService.update(id, data);
      set(() => ({
        currentUser: user,
        loadingUsers: false,
      }));
    } catch (error) {
      set(() => ({
        serverError: parseAxiosError(error),
        loadingUsers: false,
      }));
      throw error;
    }
  },
  deleteUser: async ({ id }) => {
    await axiosInstance({
      url: `/users/${id}`,
      method: 'DELETE',
    });
  },
}));
